// printImagePicker backs the print configurator rail's "Bild" control
// (components.CampaignLinkImagePicker, campaign_link_image_picker.templ).
// Deliberately NOT the CMS picker (../cms/image-picker.ts): single-select,
// read-only over a pool the server already resolved — no autosave, no
// upload, no remove-from-pool, no transactional draft/commit. select()
// dispatches a bubbling `print-image-selected` event that the rail's
// qrPrintConfig root (templates/pages/campaign_link_print.templ, Task 7b)
// listens for to update its own `image` state and re-render the preview.
//
// sources is the modal's filter-chip row: one entry per accommodation
// (key = property slug, label = property name) plus one site-wide entry
// (key = "", label = "" — siteWideLabel fills the display text for that
// chip via labelFor()). All sources start active (every chip on) so the
// grid shows the full pool until the operator narrows it down.

interface PoolImg {
  id: string;
  url?: string;
  property_slug?: string;
  search?: string;
  analyzing?: boolean;
  archived?: boolean;
  caption?: string;
  alt?: string;
  // media-04: "too_large" → no preview exists; url is the raw original.
  processing_error?: string;
}

interface Source {
  key: string;
  label: string;
}

export function printImagePicker(config: {
  pool: PoolImg[];
  sources: Source[];
  selected: string;
  siteWideLabel: string;
}) {
  return {
    pool: config.pool || [],
    sources: config.sources || [],
    selected: config.selected || "",
    siteWideLabel: config.siteWideLabel || "",
    q: "",
    // All sources on by default — the grid starts unfiltered.
    active: (config.sources || []).map((s) => s.key),

    labelFor(s: Source): string {
      return s.key ? s.label : this.siteWideLabel;
    },
    isSourceActive(key: string): boolean {
      return this.active.includes(key);
    },
    toggleSource(key: string) {
      const i = this.active.indexOf(key);
      if (i >= 0) {
        this.active.splice(i, 1);
      } else {
        this.active.push(key);
      }
    },
    // isTooLarge gates the rail's selected thumbnail: a too-large entry renders
    // the imageTooLargeThumb stand-in instead of an <img src=""> (media-04).
    isTooLarge(id: string): boolean {
      const i = this.pool.find((p) => p.id === id);
      return !!i && i.processing_error === "too_large";
    },
    urlFor(id: string): string {
      const i = this.pool.find((p) => p.id === id);
      // media-04: never hand a too-large image's raw original to a thumbnail.
      if (i && i.processing_error === "too_large") return "";
      return i ? i.url || "" : "";
    },
    filtered(): PoolImg[] {
      const q = this.q.trim().toLowerCase();
      return this.pool.filter(
        (p) =>
          this.active.includes(p.property_slug || "") &&
          (!q || (p.search || "").includes(q)),
      );
    },
    select(id: string) {
      this.selected = id;
      // $dispatch bubbles by default — this element sits inside the rail's
      // qrPrintConfig root, so no `.window` listener is needed there.
      (this as any).$dispatch("print-image-selected", { id });
      ((this as any).$refs.dlg as HTMLDialogElement | undefined)?.close();
    },
  };
}
